// src/components/Sidebar.jsx
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getActiveRole, getToken, getUserId } from '../utils/auth';
import { useReportSettings } from '../context/ReportSettingsContext';
import { useRegionalSettings } from '../context/RegionalSettingsContext';
import { isEuProductionReadinessCountry } from '../config/countryProfiles';
import { isReportEnabled, tabsForRole } from '../config/reportDefinitions';
import { useMobileNav } from '../context/MobileNavContext';
import { getFairPlayModerationSummary, getIncomingChallenges, getMyGames } from '../api/playing';
import { PLAYING_CHALLENGES_CHANGED, subscribeIncomingChallenges } from '../playing/chessSocket';
import { challengeTtlMs, isPastTtl } from '../pages/playing/playingUi';

const pathMatches = (pathname, to) => {
    if (!to) return false;
    if (pathname === to) return true;
    return pathname.startsWith(`${to}/`);
};

/** Longest matching route wins so /playing/games does not also highlight /playing. */
const activeNavPath = (pathname, items) => {
    let best = null;
    for (const item of items) {
        if (!pathMatches(pathname, item.to)) continue;
        if (!best || item.to.length > best.length) best = item.to;
    }
    return best;
};

const canHover = () =>
    typeof window !== 'undefined'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

const FLYOUT_WIDTH = 224; // w-56
const FLYOUT_GAP = 8;

const Chevron = ({ open }) => (
    <svg
        className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
    >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
);

/**
 * Parent stays fixed; submenu opens as a right-side flyout (portal) so other
 * menu rows do not shift. Hover on desktop, tap on touch.
 */
function NavGroup({ id, label, icon, items, pathname, openId, setOpenId }) {
    const btnRef = useRef(null);
    const panelRef = useRef(null);
    const hoverTimer = useRef(null);
    const activeTo = activeNavPath(pathname, items);
    const childActive = activeTo != null;
    const open = openId === id;
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const clearHoverTimer = () => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
    };

    const placePanel = useCallback(() => {
        const btn = btnRef.current;
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        const vh = window.innerHeight;
        const vw = window.innerWidth;
        let left = r.right + FLYOUT_GAP;
        if (left + FLYOUT_WIDTH > vw - 8) {
            left = Math.max(8, r.left - FLYOUT_WIDTH - FLYOUT_GAP);
        }
        // Prefer aligning with the parent row; only nudge up if the full menu
        // would otherwise clip below the viewport (no internal scrolling).
        let top = r.top;
        const panel = panelRef.current;
        const panelHeight = panel ? panel.offsetHeight : 0;
        if (panelHeight > 0 && top + panelHeight > vh - 12) {
            top = Math.max(12, vh - panelHeight - 12);
        }
        setPos({ top, left });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        placePanel();
        // Second pass after paint so we have the true content height
        const raf = requestAnimationFrame(() => placePanel());
        const onWin = () => placePanel();
        window.addEventListener('resize', onWin);
        window.addEventListener('scroll', onWin, true);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', onWin);
            window.removeEventListener('scroll', onWin, true);
        };
    }, [open, placePanel, items.length]);

    useEffect(() => () => clearHoverTimer(), []);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            const t = e.target;
            if (btnRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpenId((current) => (current === id ? null : current));
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open, id, setOpenId]);

    const openGroup = () => {
        clearHoverTimer();
        setOpenId(id);
    };

    const scheduleClose = () => {
        if (!canHover()) return;
        clearHoverTimer();
        hoverTimer.current = setTimeout(() => {
            setOpenId((current) => (current === id ? null : current));
        }, 160);
    };

    const flyout = open && typeof document !== 'undefined'
        ? createPortal(
            <div
                ref={panelRef}
                id={`nav-group-${id}`}
                role="menu"
                aria-label={label}
                className="fixed z-[80] w-56 rounded-xl border border-slate-700/90 bg-slate-900 p-2 shadow-[0_20px_50px_rgba(2,6,23,0.55)]"
                style={{ top: pos.top, left: pos.left }}
                onMouseEnter={() => {
                    if (canHover()) clearHoverTimer();
                }}
                onMouseLeave={scheduleClose}
            >
                <p className="px-2.5 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    {label}
                </p>
                <div className="space-y-0.5">
                    {items.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            role="menuitem"
                            onClick={() => setOpenId(null)}
                            className={() =>
                                `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                                    item.to === activeTo
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                }`
                            }
                        >
                            {item.icon && <span className="h-4 w-4 shrink-0">{item.icon}</span>}
                            <span className="min-w-0 flex-1 font-medium">{item.label}</span>
                            {item.badge != null && Number(item.badge) > 0 && (
                                <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                    {Number(item.badge) > 99 ? '99+' : item.badge}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </div>
            </div>,
            document.body,
        )
        : null;

    const groupBadge = items.reduce((sum, item) => sum + (Number(item.badge) > 0 ? Number(item.badge) : 0), 0);

    return (
        <div
            className="mb-1"
            onMouseEnter={() => {
                if (!canHover()) return;
                openGroup();
            }}
            onMouseLeave={scheduleClose}
        >
            <button
                ref={btnRef}
                type="button"
                aria-expanded={open}
                aria-haspopup="menu"
                aria-controls={open ? `nav-group-${id}` : undefined}
                onClick={() => setOpenId((current) => (current === id ? null : id))}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-200 ${
                    childActive || open
                        ? 'bg-primary-600/25 text-white ring-1 ring-primary-500/40'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
            >
                {icon && <span className="h-5 w-5 shrink-0">{icon}</span>}
                <span className="min-w-0 flex-1 text-sm font-medium">{label}</span>
                {groupBadge > 0 && (
                    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        {groupBadge > 99 ? '99+' : groupBadge}
                    </span>
                )}
                <Chevron open={open} />
            </button>
            {flyout}
        </div>
    );
}

const Sidebar = () => {
    const { t } = useTranslation();
    const role = getActiveRole();
    const location = useLocation();
    const { isOpen: mobileOpen, close: onClose } = useMobileNav();
    const { settings: reportSettings, ready: reportSettingsReady } = useReportSettings();
    const { settings: regionalSettings, ready: regionalReady } = useRegionalSettings();
    const showEuProductionReady = regionalReady
        && isEuProductionReadinessCountry(regionalSettings?.countryCode);
    const hasHubReports = reportSettingsReady && tabsForRole(role, reportSettings).length > 0;
    const sessionStatementEnabled = reportSettingsReady && isReportEnabled(reportSettings, 'session-statement');
    const [openId, setOpenId] = useState(null);
    const [fairPlayOpenTotal, setFairPlayOpenTotal] = useState(0);
    const [incomingChallengeCount, setIncomingChallengeCount] = useState(0);
    const [yourMoveCount, setYourMoveCount] = useState(0);

    useEffect(() => {
        if (!(role === 'ADMIN' || role === 'MANAGER' || role === 'COACH')) {
            setFairPlayOpenTotal(0);
            return undefined;
        }
        if (!getToken()) return undefined;
        let cancelled = false;
        const load = () => {
            getFairPlayModerationSummary()
                .then((s) => {
                    if (!cancelled) setFairPlayOpenTotal(Number(s?.openTotal) || 0);
                })
                .catch(() => {
                    // Keep last known count — zeroing on failure hides open work.
                });
        };
        load();
        const iv = setInterval(load, 60000);
        const onVis = () => {
            if (document.visibilityState === 'visible') load();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            cancelled = true;
            clearInterval(iv);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [role, location.pathname]);

    useEffect(() => {
        if (!getToken()) {
            setIncomingChallengeCount(0);
            return undefined;
        }
        let cancelled = false;
        const load = () => {
            getIncomingChallenges()
                .then((rows) => {
                    if (cancelled) return;
                    const list = Array.isArray(rows) ? rows : [];
                    const live = list.filter((c) => !isPastTtl(c.createdAt, challengeTtlMs(c)));
                    setIncomingChallengeCount(live.length);
                })
                .catch(() => {
                    // Keep last known count — zeroing on failure hides open invites.
                });
        };
        load();
        const iv = setInterval(load, 60000);
        const onVis = () => {
            if (document.visibilityState === 'visible') load();
        };
        const onChanged = () => load();
        document.addEventListener('visibilitychange', onVis);
        window.addEventListener(PLAYING_CHALLENGES_CHANGED, onChanged);
        const uid = getUserId();
        const unsub = uid != null
            ? subscribeIncomingChallenges(uid, () => load())
            : () => {};
        return () => {
            cancelled = true;
            clearInterval(iv);
            document.removeEventListener('visibilitychange', onVis);
            window.removeEventListener(PLAYING_CHALLENGES_CHANGED, onChanged);
            unsub();
        };
    }, [role, location.pathname]);

    useEffect(() => {
        if (!getToken()) {
            setYourMoveCount(0);
            return undefined;
        }
        let cancelled = false;
        const load = () => {
            getMyGames()
                .then((rows) => {
                    if (cancelled) return;
                    const list = Array.isArray(rows) ? rows : (Array.isArray(rows?.games) ? rows.games : []);
                    const finished = new Set(['WHITE_WON', 'BLACK_WON', 'DRAW', 'ABORTED']);
                    const n = list.filter((g) => {
                        const st = String(g?.status || '').toUpperCase();
                        if (finished.has(st) || g?.endedAt) return false;
                        return !!g?.myTurn && (st === 'ACTIVE' || st === 'PENDING');
                    }).length;
                    setYourMoveCount(n);
                })
                .catch(() => {
                    // Keep last known count.
                });
        };
        load();
        const iv = setInterval(load, 60000);
        const onVis = () => {
            if (document.visibilityState === 'visible') load();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            cancelled = true;
            clearInterval(iv);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [role, location.pathname]);

    useEffect(() => {
        if (onClose) onClose();
        setOpenId(null);
    }, [location.pathname, onClose]);

    const navItemClass = ({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${isActive
            ? 'bg-primary-600 text-white shadow-sm'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`;

    const dashboardPath =
        role === 'ADMIN' ? '/admin' :
            role === 'MANAGER' ? '/manager' :
                role === 'COACH' ? '/coach' :
                    role === 'STUDENT' ? '/student' :
                        role === 'PARENT' ? '/parent' :
                            '/';

    const Section = ({ title, children }) => (
        <div className="mb-5">
            {title ? (
                <div className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    {title}
                </div>
            ) : null}
            <div className="space-y-1">{children}</div>
        </div>
    );

    const NavItem = ({ to, icon, children, end }) => (
        <NavLink to={to} end={end} className={navItemClass}>
            {icon && <span className="h-5 w-5 shrink-0">{icon}</span>}
            <span className="text-sm font-medium">{children}</span>
        </NavLink>
    );

    const icons = useMemo(() => ({
        dashboard: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
        ),
        branches: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
        ),
        users: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
        ),
        levels: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
        ),
        batches: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
        ),
        enrollments: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        ),
        sessions: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        ),
        reports: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
        ),
        wallet: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
        ),
        branding: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
        ),
        payments: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2m0-6h2m-2 0a2 2 0 012-2h1m-3 2a2 2 0 002 2h1" />
            </svg>
        ),
        calendar: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        ),
        notifications: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
        ),
        discount: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
        ),
        warning: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
        ),
        email: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        ),
        chat: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
        ),
        shield: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        ),
        gear: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
        ),
        chess: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l1.5 4.5H18l-3.5 2.5L16 14H8l1.5-5L6 6.5h4.5L12 2zM8 16h8v2H8v-2zm-1 3h10v2H7v-2z" />
            </svg>
        ),
        trophy: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 21h8M12 17v4M7 4h10v4a5 5 0 01-10 0V4zm-3 1h3v2a4 4 0 01-3-2zm13 0h3a4 4 0 01-3 2V5z" />
            </svg>
        ),
        ops: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
            </svg>
        ),
        video: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
        ),
    }), []);

    const groupProps = { pathname: location.pathname, openId, setOpenId };

    const playingPlatformItems = [
        { to: '/playing', label: t('nav.playingHub'), icon: icons.chess },
        { to: '/playing/play', label: t('nav.playNow'), icon: icons.chess },
        { to: '/playing/live', label: t('nav.liveGames'), icon: icons.sessions },
        { to: '/playing/games', label: t('nav.myGames'), icon: icons.sessions, badge: yourMoveCount },
        ...(role === 'PARENT'
          ? []
          : [{ to: '/playing/challenge', label: t('nav.challenges'), icon: icons.users, badge: incomingChallengeCount }]),
        { to: '/playing/leaderboard', label: t('nav.leaderboard'), icon: icons.reports },
        { to: '/playing/ai-coach', label: t('nav.aiCoach', { defaultValue: 'Ask AI Coach' }), icon: icons.chess },
        ...((role === 'ADMIN' || role === 'MANAGER' || role === 'COACH')
          ? [
            {
              to: '/playing/fair-play',
              label: t('nav.fairPlay'),
              icon: icons.reports,
              badge: fairPlayOpenTotal,
            },
          ]
          : []),
    ];

    const classroomBase =
        role === 'ADMIN' ? '/admin/classroom'
            : role === 'MANAGER' ? '/manager/classroom'
                : role === 'COACH' ? '/coach/classroom'
                    : role === 'PARENT' ? '/parent/classroom'
                        : role === 'STUDENT' ? '/student/classroom'
                            : null;

    const classroomItems = classroomBase
        ? [
            { to: classroomBase, label: t('nav.classroomOverview'), icon: icons.dashboard },
            { to: `${classroomBase}/live`, label: t('nav.classroomLive'), icon: icons.video },
            ...(role === 'ADMIN'
                ? [
                    { to: `${classroomBase}/zoom`, label: t('nav.classroomZoom'), icon: icons.gear },
                    { to: `${classroomBase}/settings`, label: t('nav.classroomSettings'), icon: icons.gear },
                ]
                : []),
        ]
        : [];

    return (
        <aside
            id="app-sidebar"
            className={`
            fixed lg:static inset-y-0 left-0 z-40
            flex w-[280px] lg:w-72 flex-col overflow-hidden
            border-r border-slate-800/60
            bg-gradient-to-b from-slate-950 via-slate-900 to-slate-900
            text-white shadow-[0_24px_55px_rgba(2,6,23,0.28)]
            transition-transform duration-300 ease-in-out
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
            <div className="relative border-b border-white/10 p-4">
                <button
                    onClick={onClose}
                    className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 lg:hidden"
                    aria-label={t('header.closeNavSidebar')}
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <NavLink
                    to={dashboardPath}
                    end
                    className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-primary-600 via-primary-500 to-indigo-600 p-3 text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl"
                >
                    <span className="h-5 w-5 shrink-0">{icons.dashboard}</span>
                    <span className="text-lg font-bold">{t('nav.dashboard')}</span>
                </NavLink>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 scrollbar-thin">
                {role === 'ADMIN' && (
                    <Section>
                        <NavGroup
                            id="playing"
                            label={t('nav.playingPlatform')}
                            icon={icons.chess}
                            {...groupProps}
                            items={playingPlatformItems}
                        />
                        <NavGroup
                            id="classroom"
                            label={t('nav.classroom')}
                            icon={icons.video}
                            {...groupProps}
                            items={classroomItems}
                        />
                        <NavGroup
                            id="people"
                            label={t('nav.people')}
                            icon={icons.users}
                            {...groupProps}
                            items={[
                                { to: '/admin/managers', label: t('nav.managers'), icon: icons.users },
                                { to: '/admin/coaches', label: t('nav.coaches'), icon: icons.users },
                                { to: '/admin/students', label: t('nav.students'), icon: icons.users },
                            ]}
                        />
                        <NavGroup
                            id="academy"
                            label={t('nav.academySetup')}
                            icon={icons.branches}
                            {...groupProps}
                            items={[
                                { to: '/admin/branches', label: t('nav.branches'), icon: icons.branches },
                                { to: '/admin/levels', label: t('nav.levels'), icon: icons.levels },
                                { to: '/admin/batches', label: t('nav.batches'), icon: icons.batches },
                            ]}
                        />
                        <NavGroup
                            id="operations"
                            label={t('nav.operations')}
                            icon={icons.ops}
                            {...groupProps}
                            items={[
                                { to: '/admin/enrollments', label: t('nav.enrollments'), icon: icons.enrollments },
                                { to: '/admin/sessions', label: t('nav.sessions'), icon: icons.sessions },
                                { to: '/admin/calendar', label: t('nav.calendar'), icon: icons.calendar },
                                { to: '/admin/discounts', label: t('nav.discounts'), icon: icons.discount },
                            ]}
                        />
                        <NavGroup
                            id="reports"
                            label={t('nav.reports')}
                            icon={icons.reports}
                            {...groupProps}
                            items={[
                                { to: '/admin/reports', label: t('nav.reports'), icon: icons.reports },
                                { to: '/admin/report-settings', label: t('nav.reportSettings'), icon: icons.reports },
                                { to: '/admin/at-risk', label: t('nav.atRiskStudents'), icon: icons.warning },
                            ]}
                        />
                        <NavGroup
                            id="fees"
                            label={t('nav.feesBilling')}
                            icon={icons.wallet}
                            {...groupProps}
                            items={[
                                { to: '/admin/advance-payment', label: t('nav.walletTopup'), icon: icons.wallet },
                                { to: '/admin/billing-settings', label: t('nav.paymentEnforcement'), icon: icons.payments },
                            ]}
                        />
                        <NavGroup
                            id="system"
                            label={t('nav.system')}
                            icon={icons.gear}
                            {...groupProps}
                            items={[
                                { to: '/admin/regional-settings', label: t('nav.regionalSettings'), icon: icons.gear },
                                ...(showEuProductionReady
                                    ? [{ to: '/admin/production-readiness', label: t('nav.productionReadiness'), icon: icons.shield }]
                                    : []),
                                { to: '/admin/payment-methods', label: t('nav.paymentMethods'), icon: icons.payments },
                                { to: '/admin/email-settings', label: t('nav.emailSettings'), icon: icons.email },
                                { to: '/admin/whatsapp', label: t('nav.whatsapp'), icon: icons.chat },
                                { to: '/admin/ai-coach-settings', label: t('nav.aiCoachSettings'), icon: icons.chat },
                                { to: '/admin/playing-platform-settings', label: t('nav.playingPlatformSettings', { defaultValue: 'Playing platform' }), icon: icons.gear },
                                { to: '/admin/branding', label: t('nav.branding'), icon: icons.branding },
                                { to: '/admin/license', label: t('nav.license'), icon: icons.shield },
                                { to: '/admin/holidays', label: t('nav.holidays'), icon: icons.calendar },
                                { to: '/admin/notification-preferences', label: t('nav.notifications'), icon: icons.notifications },
                                { to: '/admin/audit-logs', label: t('nav.auditLogs'), icon: icons.reports },
                            ]}
                        />
                        <NavItem to="/admin/faq" icon={icons.reports}>{t('nav.faq')}</NavItem>
                    </Section>
                )}

                {role === 'MANAGER' && (
                    <Section>
                        <NavGroup
                            id="playing"
                            label={t('nav.playingPlatform')}
                            icon={icons.chess}
                            {...groupProps}
                            items={playingPlatformItems}
                        />
                        <NavGroup
                            id="classroom"
                            label={t('nav.classroom')}
                            icon={icons.video}
                            {...groupProps}
                            items={classroomItems}
                        />
                        <NavGroup
                            id="m-people"
                            label={t('nav.people')}
                            icon={icons.users}
                            {...groupProps}
                            items={[
                                { to: '/manager/coaches', label: t('nav.coaches'), icon: icons.users },
                                { to: '/manager/students', label: t('nav.students'), icon: icons.users },
                            ]}
                        />
                        <NavGroup
                            id="m-academy"
                            label={t('nav.academySetup')}
                            icon={icons.branches}
                            {...groupProps}
                            items={[
                                { to: '/manager/branches', label: t('nav.branches'), icon: icons.branches },
                                { to: '/manager/levels', label: t('nav.levels'), icon: icons.levels },
                                { to: '/manager/batches', label: t('nav.batches'), icon: icons.batches },
                            ]}
                        />
                        <NavGroup
                            id="m-operations"
                            label={t('nav.operations')}
                            icon={icons.ops}
                            {...groupProps}
                            items={[
                                { to: '/manager/enrollments', label: t('nav.enrollments'), icon: icons.enrollments },
                                { to: '/manager/sessions', label: t('nav.sessions'), icon: icons.sessions },
                                { to: '/manager/calendar', label: t('nav.calendar'), icon: icons.calendar },
                            ]}
                        />
                        <NavGroup
                            id="m-fees"
                            label={t('nav.feesBilling')}
                            icon={icons.wallet}
                            {...groupProps}
                            items={[
                                { to: '/manager/advance-payment', label: t('nav.walletTopup'), icon: icons.wallet },
                            ]}
                        />
                        {hasHubReports && (
                            <NavGroup
                                id="m-reports"
                                label={t('nav.reports')}
                                icon={icons.reports}
                                {...groupProps}
                                items={[
                                    { to: '/manager/reports', label: t('nav.reports'), icon: icons.reports },
                                ]}
                            />
                        )}
                        <NavItem to="/manager/faq" icon={icons.reports}>{t('nav.faq')}</NavItem>
                    </Section>
                )}

                {role === 'COACH' && (
                    <Section>
                        <NavGroup
                            id="playing"
                            label={t('nav.playingPlatform')}
                            icon={icons.chess}
                            {...groupProps}
                            items={playingPlatformItems}
                        />
                        <NavGroup
                            id="classroom"
                            label={t('nav.classroom')}
                            icon={icons.video}
                            {...groupProps}
                            items={classroomItems}
                        />
                        <NavGroup
                            id="c-people"
                            label={t('nav.people')}
                            icon={icons.users}
                            {...groupProps}
                            items={[
                                { to: '/coach/students', label: t('nav.students'), icon: icons.users },
                            ]}
                        />
                        <NavGroup
                            id="c-academy"
                            label={t('nav.academySetup')}
                            icon={icons.branches}
                            {...groupProps}
                            items={[
                                { to: '/coach/levels', label: t('nav.levels'), icon: icons.levels },
                            ]}
                        />
                        <NavGroup
                            id="c-operations"
                            label={t('nav.operations')}
                            icon={icons.ops}
                            {...groupProps}
                            items={[
                                { to: '/coach/enrollments', label: t('nav.enrollments'), icon: icons.enrollments },
                                { to: '/coach/sessions', label: t('nav.sessions'), icon: icons.sessions },
                                { to: '/coach/calendar', label: t('nav.calendar'), icon: icons.calendar },
                            ]}
                        />
                        {hasHubReports && (
                            <NavGroup
                                id="c-reports"
                                label={t('nav.reports')}
                                icon={icons.reports}
                                {...groupProps}
                                items={[
                                    { to: '/coach/reports', label: t('nav.reports'), icon: icons.reports },
                                ]}
                            />
                        )}
                        <NavItem to="/coach/faq" icon={icons.reports}>{t('nav.faq')}</NavItem>
                    </Section>
                )}

                {role === 'PARENT' && (
                    <Section>
                        <NavGroup
                            id="playing"
                            label={t('nav.playingPlatform')}
                            icon={icons.chess}
                            {...groupProps}
                            items={playingPlatformItems}
                        />
                        <NavGroup
                            id="classroom"
                            label={t('nav.classroom')}
                            icon={icons.video}
                            {...groupProps}
                            items={classroomItems}
                        />
                        <NavGroup
                            id="p-child"
                            label={t('nav.myChild')}
                            icon={icons.users}
                            {...groupProps}
                            items={[
                                { to: '/parent', label: t('nav.home'), icon: icons.dashboard },
                                { to: '/parent/dashboard', label: t('nav.overview'), icon: icons.dashboard },
                                { to: '/parent/calendar', label: t('nav.calendar'), icon: icons.calendar },
                                { to: '/parent/attendance', label: t('nav.attendance'), icon: icons.sessions },
                                { to: '/parent/wallet', label: t('nav.wallet'), icon: icons.wallet },
                            ]}
                        />
                        <NavGroup
                            id="p-operations"
                            label={t('nav.operations')}
                            icon={icons.ops}
                            {...groupProps}
                            items={[
                                { to: '/parent/enrollments', label: t('nav.enrollments'), icon: icons.enrollments },
                                { to: '/parent/sessions', label: t('nav.sessions'), icon: icons.sessions },
                                ...(sessionStatementEnabled
                                    ? [{ to: '/parent/session-statement', label: t('nav.sessionStatement'), icon: icons.reports }]
                                    : []),
                            ]}
                        />
                        <NavGroup
                            id="p-settings"
                            label={t('nav.settings')}
                            icon={icons.gear}
                            {...groupProps}
                            items={[
                                { to: '/parent/notifications', label: t('nav.notifications'), icon: icons.notifications },
                            ]}
                        />
                        <NavItem to="/parent/faq" icon={icons.reports}>{t('nav.faq')}</NavItem>
                    </Section>
                )}

                {role === 'STUDENT' && (
                    <Section>
                        <NavGroup
                            id="playing"
                            label={t('nav.playingPlatform')}
                            icon={icons.chess}
                            {...groupProps}
                            items={playingPlatformItems}
                        />
                        <NavGroup
                            id="classroom"
                            label={t('nav.classroom')}
                            icon={icons.video}
                            {...groupProps}
                            items={classroomItems}
                        />
                        <NavGroup
                            id="s-operations"
                            label={t('nav.operations')}
                            icon={icons.ops}
                            {...groupProps}
                            items={[
                                { to: '/student/sessions', label: t('nav.mySessions'), icon: icons.sessions },
                                { to: '/student/calendar', label: t('nav.calendar'), icon: icons.calendar },
                                { to: '/student/enrollments', label: t('nav.myEnrollments'), icon: icons.enrollments },
                                ...(sessionStatementEnabled
                                    ? [{ to: '/student/session-statement', label: t('nav.sessionStatement'), icon: icons.reports }]
                                    : []),
                            ]}
                        />
                        <NavGroup
                            id="s-fees"
                            label={t('nav.feesBilling')}
                            icon={icons.wallet}
                            {...groupProps}
                            items={[
                                { to: '/student/wallet', label: t('nav.wallet'), icon: icons.wallet },
                            ]}
                        />
                        <NavGroup
                            id="s-settings"
                            label={t('nav.settings')}
                            icon={icons.gear}
                            {...groupProps}
                            items={[
                                { to: '/student/notifications', label: t('nav.notifications'), icon: icons.notifications },
                            ]}
                        />
                        <NavItem to="/student/faq" icon={icons.reports}>{t('nav.faq')}</NavItem>
                    </Section>
                )}
            </nav>
        </aside>
    );
};

export default Sidebar;
